import { type ReactNode } from 'react'
import Box from '@mui/material/Box'
import { ringFraction } from '../utilities/usageAxis'

export type Ring = {
  label: string
  color: string
  used: number
  included: number | null // null ⇒ unlimited (drawn against `unlimitedScale`, never full)
}

type Props = {
  rings: Ring[] // outer → inner
  size?: number // default: whatever keeps the center hole at `rings.length` rings
  children?: ReactNode // center overlay (e.g. projected spend)
}

const STROKE = 14
const GAP = 6

// The center hole three rings left at the original fixed 200 px — the room the projected-spend
// overlay needs. Rings are laid out INWARD from the edge, so an extra ring eats the hole rather
// than the margin: at a fixed size a fourth ring would be drawn through the number in the middle.
// The default size therefore GROWS with the ring count and holds the hole steady — 3 rings still
// give exactly 200, so this is not a change to any existing figure (app#152).
const HOLE_RADIUS = 46
const defaultSize = (count: number): number => 2 * (HOLE_RADIUS + STROKE + Math.max(0, count - 1) * (STROKE + GAP))

// Apple-Activity-Rings-style concentric donuts: one ring per pooled allowance, each filling
// clockwise as used/included. Over 100% (paid overage) the ring fills full and glows rather
// than capping — usage billing tracks, it doesn't wall. Pure SVG, no chart dependency (PLAN_2.6).
export default function UsageRings({ rings, size = defaultSize(rings.length), children }: Readonly<Props>) {
  const center = size / 2
  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0
      }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${center} ${center})`}>
          {rings.map((ring, i) => {
            const radius = center - STROKE / 2 - i * (STROKE + GAP)
            const circumference = 2 * Math.PI * radius
            // ⚠ `included` has THREE states and 0 is not one of the other two — see
            // `allowanceFraction`. The arm this replaced (`included && included > 0 ? … : 0`) drew
            // a zero-allowance axis as an EMPTY ring, which reads as healthy. An unlimited axis is
            // drawn against a scale that grows with the count — see `unlimitedScale`.
            const fraction = ringFraction(ring.used, ring.included)
            const filled = Math.min(fraction, 1) * circumference
            const over = fraction > 1
            return (
              <g key={ring.label}>
                <circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke={ring.color}
                  strokeOpacity={0.15}
                  strokeWidth={STROKE}
                />
                <circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke={ring.color}
                  strokeWidth={STROKE}
                  strokeLinecap="round"
                  strokeDasharray={`${filled} ${circumference - filled}`}
                  style={over ? { filter: `drop-shadow(0 0 5px ${ring.color})` } : undefined}
                />
              </g>
            )
          })}
        </g>
      </svg>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: size,
          height: size,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}>
        {children}
      </Box>
    </Box>
  )
}
